// /register — request an invite by email or by text message.
//
// The SMS channel was complete in the server and the shared types from Phase 7
// and offered by no form, so `method: 'sms'` was unreachable and no account the
// SPA created could ever have a phone number. See docs/migration-gaps.md item 2:
// `phone` was written on exactly one line, this flow's INSERT, and no form could
// reach it.
//
// Registering by SMS also PROVES the number, which is why an account created this
// way lands with `phone_verified = 1` — the invite code went to that number and
// came back. The account page's verification flow exists for every other case.

import { ApiClientError, register } from '../api/client.js';
import { type View } from '../router.js';
import { el } from '../dom.js';
import { devPanel } from './devPanel.js';
import { getRecaptchaToken, preloadRecaptcha } from '../recaptcha.js';
import { authBrand } from './authBrand.js';

export const registerView: View = (outlet, ctx) => {
  preloadRecaptcha();

  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Create account');

  const email = el('input', {
    id: 'email',
    name: 'email',
    type: 'email',
    required: true,
    autocomplete: 'email',
    inputmode: 'email',
    placeholder: 'you@example.com',
  }) as HTMLInputElement;

  const phone = el('input', {
    id: 'phone',
    name: 'phone',
    type: 'tel',
    autocomplete: 'tel',
    inputmode: 'tel',
    placeholder: '555 123 4567',
  }) as HTMLInputElement;

  // Radios rather than a select: two options, and the choice changes which field
  // below is required, so it wants to be visible rather than folded away.
  const byEmail = el('input', {
    type: 'radio',
    name: 'method',
    id: 'method-email',
    value: 'email',
  }) as HTMLInputElement;
  const bySms = el('input', {
    type: 'radio',
    name: 'method',
    id: 'method-sms',
    value: 'sms',
  }) as HTMLInputElement;
  // Defaults to text message, not email: this is the path a TCR reviewer needs
  // to reach with nothing else to click, since it's the one carrying the A2P
  // consent disclosure they're verifying. See docs/a2p-campaign.md.
  bySms.checked = true;

  const emailRow = el('div', { class: 'field' }, [el('label', { for: 'email' }, 'Email'), email]);
  const phoneRow = el('div', { class: 'field' }, [
    el('label', { for: 'phone' }, 'Mobile number'),
    phone,
    el('p', { class: 'muted small' }, 'We text you a verification code to finish setting up the account.'),
    // The A2P 10DLC consent disclosure, visible at the point of opt-in rather
    // than buried in Terms — this is what a carrier reviewer checks the CTA
    // against. Wording must stay in sync with the registered consent text and
    // SMS program terms in docs/a2p-campaign.md; do not reword independently.
    el('p', { class: 'muted small' }, [
      'By entering your number, you consent to receive text messages from MySocialCal, ',
      'including this verification code. Msg & data rates may apply. Reply STOP to opt out, ',
      'HELP for help. See our ',
      el('a', { href: '/privacy' }, 'Privacy Policy'),
      ' and ',
      el('a', { href: '/terms' }, 'Terms and Conditions'),
      '.',
    ]),
  ]);

  const inviteCode = el('input', {
    id: 'inviteCode',
    name: 'inviteCode',
    type: 'text',
    autocomplete: 'off',
    placeholder: 'Invite code',
    // A join link lands here with ?code=. Prefilled and still editable.
    value: ctx.query.get('code') ?? '',
  }) as HTMLInputElement;
  // Shown and honored on both paths as of 2026-08-25: a group-scoped code
  // routes a self-registration straight into that group, by email or by SMS.
  // Never required on the SMS path — see registrationRoutes.ts — a TCR
  // reviewer (or anyone) must be able to reach and submit that screen with
  // nothing else to click.
  const inviteCodeRow = el('div', { class: 'field' }, [
    el('label', { for: 'inviteCode' }, 'Invite code (optional)'),
    inviteCode,
    el('p', { class: 'muted small' }, "If an organizer gave you a group code, enter it here to join their group directly."),
  ]);

  function renderMethod(): void {
    const sms = bySms.checked;
    emailRow.hidden = sms;
    phoneRow.hidden = !sms;
    // `required` follows the visible field. Left on the hidden one, the browser
    // would block submit on a control nobody can see — and `novalidate` on the
    // form only silences the bubble, not the validity state other code reads.
    email.required = !sms;
    phone.required = sms;
  }
  byEmail.addEventListener('change', renderMethod);
  bySms.addEventListener('change', renderMethod);
  renderMethod();

  // Honeypot: kept off-screen and out of the tab order. Bots fill it; humans
  // never see it. A filled value makes the server respond like success and send
  // nothing.
  const honeypot = el('input', {
    name: 'website',
    type: 'text',
    tabindex: -1,
    autocomplete: 'off',
    style: 'position:absolute;left:-9999px;width:1px;height:1px;',
    'aria-hidden': 'true',
  }) as HTMLInputElement;

  const form = el('form', { method: 'post', action: '/register', novalidate: true }, [
    el('fieldset', { class: 'method-choice' }, [
      el('legend', {}, 'How should we reach you?'),
      el('label', { class: 'check', for: 'method-email' }, [byEmail, ' Email']),
      el('label', { class: 'check', for: 'method-sms' }, [bySms, ' Text message']),
    ]),
    emailRow,
    phoneRow,
    inviteCodeRow,
    honeypot,
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    try {
      const sms = bySms.checked;
      const recaptchaToken = await getRecaptchaToken('register');
      const { message, devVerifyUrl, devVerifyCode } = await register({
        method: sms ? 'sms' : 'email',
        // Only the chosen channel's field is sent. Sending both would let a
        // stale value in the hidden one reach the server, which decides which
        // identifier to use from `method` and would happily store the other.
        ...(sms ? { phone: phone.value.trim() } : { email: email.value.trim() }),
        inviteCode: inviteCode.value.trim() || undefined,
        recaptchaToken,
        website: honeypot.value || undefined,
      });
      outlet.replaceChildren(
        el('main', { class: 'auth-card' }, [
          authBrand('h1'),
          el('p', {}, message),
          // SMS has no link to click through — send the user to /verify to type
          // the code back in. Email's link goes straight there with ?code= set.
          ...(sms ? [el('p', {}, [el('a', { href: '/verify' }, 'Enter your code')])] : []),
          ...devPanel({ url: devVerifyUrl, code: devVerifyCode, linkText: 'Open the verification link' }),
          el('p', {}, [el('a', { href: '/login' }, 'Back to log in')]),
        ]),
      );
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  outlet.append(
    el('main', { class: 'auth-card' }, [
      authBrand('h1'),
      el('p', {}, 'Create your account'),
      form,
      el('p', {}, [el('a', { href: '/login' }, 'Already have an account? Log in')]),
    ]),
  );
};
